import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchKpiExecutiveOverview,
  KpiExecutiveOverviewResponse,
} from '../services/onTimeDispatchKpiApi';
import type { AppliedFiltersSnapshot } from '../../types/filterTypes';

export interface UseOnTimeDispatchKpiReturn {
  kpiData: KpiExecutiveOverviewResponse | null;
  kpiLoading: boolean;
  kpiError: string | null;
  refetchKpi: () => void;
}

/**
 * Hook for fetching KPI data based on applied filters.
 * Auto-fetches when filters change and tab is active.
 */
export function useOnTimeDispatchKpi(
  appliedFilters: AppliedFiltersSnapshot,
  isTabActive: boolean,
  adminLocations: any[]
): UseOnTimeDispatchKpiReturn {
  const [kpiData, setKpiData] = useState<KpiExecutiveOverviewResponse | null>(null);
  const [kpiLoading, setKpiLoading] = useState(false);
  const [kpiError, setKpiError] = useState<string | null>(null);

  const isMountedRef = useRef(true);
  const lastRequestRef = useRef<string>('');

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const doFetch = useCallback(() => {
    if (!isTabActive || !appliedFilters) return;

    // Reset dedup key so retry always fires
    lastRequestRef.current = '';

    setKpiLoading(true);
    setKpiError(null);

    console.log('[KPI Hook] Fetching with filters:', appliedFilters);

    fetchKpiExecutiveOverview(appliedFilters, adminLocations)
      .then((response) => {
        if (!isMountedRef.current) return;
        console.log('[KPI Hook] Data received:', response);
        setKpiData(response);
      })
      .catch((err: unknown) => {
        if (!isMountedRef.current) return;
        console.error('[KPI Hook] Error:', err);
        const message = err instanceof Error ? err.message : 'Failed to load KPI data';
        setKpiError(message);
      })
      .finally(() => {
        if (isMountedRef.current) setKpiLoading(false);
      });
  }, [isTabActive, appliedFilters, adminLocations]);

  // Fetch whenever appliedFilters changes AND tab is active
  useEffect(() => {
    if (!isTabActive || !appliedFilters) return;

    // Deduplicate requests using JSON stringification
    const requestKey = JSON.stringify({ appliedFilters, adminLocations });
    if (requestKey === lastRequestRef.current) return;
    lastRequestRef.current = requestKey;

    setKpiLoading(true);
    setKpiError(null);

    console.log('[KPI Hook] Fetching with filters:', appliedFilters);

    fetchKpiExecutiveOverview(appliedFilters, adminLocations)
      .then((response) => {
        if (!isMountedRef.current) return;
        console.log('[KPI Hook] Data received:', response);
        setKpiData(response);
      })
      .catch((err: unknown) => {
        if (!isMountedRef.current) return;
        console.error('[KPI Hook] Error:', err);
        const message = err instanceof Error ? err.message : 'Failed to load KPI data';
        setKpiError(message);
      })
      .finally(() => {
        if (isMountedRef.current) setKpiLoading(false);
      });
  }, [isTabActive, appliedFilters, adminLocations]);

  return { kpiData, kpiLoading, kpiError, refetchKpi: doFetch };
}
